<div class="prompt-tuning-container">
  <app-page-header></app-page-header>

  <div class="evaluation-section-header">
    <div class="evaluation-section-title-wrap">
      <button
        class="header-back-btn section-back-btn"
        (click)="navigateBackFromPromptTuning()"
        [title]="promptTuningBackTooltip"
        [attr.aria-label]="promptTuningBackTooltip">
        <svg
          class="back-arrow-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <path d="M19 12H5"></path>
          <path d="M12 19l-7-7 7-7"></path>
        </svg>
      </button>
      <h2 class="evaluation-section-title">Prompt Tuning</h2>
    </div>
  </div>

  <!-- Three Column Layout -->
  <div
    #contentGrid
    class="content-grid"
    [class.file-column-collapsed]="isFileColumnCollapsed"
    [class.layout-ready]="isColumnLayoutReady"
    [class.column-resize-enabled]="isAnyColumnResizeEnabled()"
    [class.column-resize-dragging]="isColumnResizeDragging"
    [ngStyle]="getContentGridStyles()">
    
    <!-- Column 1: Files Section -->
    <section class="column files-column" [class.collapsed]="isFileColumnCollapsed">
      <!-- Collapsed State: Show selected file indicator -->
      @if (isFileColumnCollapsed && selectedFile) {
        <div class="collapsed-file-indicator" (click)="toggleFileColumn()" [title]="selectedFile.fileName">
          <button class="btn-expand-files" title="Expand file list">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
          <div class="selected-file-name-vertical">
            <span>{{ selectedFile.fileName }}</span>
          </div>
        </div>
      }
      
      <!-- Expanded State: Show full file list -->
      <div class="card" [class.hidden]="isFileColumnCollapsed">
        <!-- Document Title with Collapse Button -->
        <div class="document-header">
          <h3 class="document-title" title="{{ documentModel }} ({{ files.length }} {{ files.length === 1 ? 'File' : 'Files' }})">{{ documentModel }} ({{ files.length }} {{ files.length === 1 ? 'File' : 'Files' }})</h3>
          @if (selectedFile) {
            <button class="btn-collapse-files" (click)="toggleFileColumn()" title="Collapse file list">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
          }
        </div>

        <div class="file-tree">
          @if (loadingEvalFiles) {
            <div class="files-loading-state">
              <div class="beat-loader">
                <div class="beat"></div>
                <div class="beat"></div>
                <div class="beat"></div>
                <div class="beat"></div>
                <div class="beat"></div>
              </div>
              <span class="loading-text">Loading files...</span>
            </div>
          } @else if (files.length > 0) {
            @for (file of files; track file.id) {
              <div class="tree-item file-item" 
                   [class.selected]="selectedFile?.id === file.id"
                   (click)="selectFileOnly(file)"
                   [title]="file.fileName">
                <div class="tree-row">
                  @if (getFileStatus(file.id)) {
                    <span class="status-dot"
                          [class]="'status-dot ' + getFileStatusClass(file.id)"
                          [title]="getFileStatus(file.id)?.tooltip"
                          role="status"
                          [attr.aria-label]="getFileStatus(file.id)?.tooltip">
                    </span>
                  }
                  <svg class="file-icon pdf-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                  <span class="tree-label">{{ file.fileName }}</span>
                </div>
              </div>
            }
          } @else {
            <div class="empty-state-small">
              <p>No PDF files uploaded yet</p>
              <p class="empty-subtitle">Click Upload to add files</p>
            </div>
          }
        </div>
      </div>
    </section>

    @if (canResizeSplitter('files-viewer')) {
      <button
        type="button"
        class="column-resizer column-resizer-left"
        title="Resize files and preview panel"
        aria-label="Resize files and preview panel"
        (pointerdown)="startColumnResize($event, 'files-viewer')">
        <span class="column-resizer-grip" aria-hidden="true"></span>
      </button>
    }
    
    <!-- Column 2: Preview Section -->
    <section class="column viewer-column">
      <div class="card viewer-card">
        <div class="viewer-header-inline">
          <h2 class="card-title">Preview</h2>
          <div class="viewer-controls-inline">
            <div class="page-controls">
              <button class="control-btn" (click)="previousPage()" [disabled]="!pdfSrc || currentPage === 1" title="Previous Page">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <input 
                type="text" 
                class="page-input"
                [(ngModel)]="pageInput"
                (blur)="goToPage()"
                (keydown)="onPageInputKeydown($event)"
                [disabled]="!pdfSrc"
                title="Go to page">
              <span class="page-separator">/</span>
              <span class="page-total">{{ totalPages || 0 }}</span>
              <button class="control-btn" (click)="nextPage()" [disabled]="!pdfSrc || currentPage === totalPages" title="Next Page">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
            <div class="zoom-controls">
              <button class="control-btn" (click)="zoomOut()" [disabled]="!pdfSrc || zoom <= 0.5" title="Zoom Out">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
              <input 
                type="text" 
                class="zoom-input"
                [(ngModel)]="zoomPercentage"
                (blur)="setZoomByPercentage()"
                (keydown)="onZoomInputKeydown($event)"
                [disabled]="!pdfSrc"
                title="Enter zoom percentage (50-300)">
              <span class="zoom-percent-sign">%</span>
              <button class="control-btn" (click)="zoomIn()" [disabled]="!pdfSrc || zoom >= 3.0" title="Zoom In">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="11" y1="8" x2="11" y2="14"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
            </div>
            <div class="rotate-controls">
              <button class="control-btn rotate-btn" (click)="rotatePdf()" [disabled]="!pdfSrc" title="Rotate 90° clockwise">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path>
                  <path d="M21 3v5h-5"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div class="pdf-container" #pdfContainer>
          @if (!pdfSrc && !isLoadingPdf) {
            <div class="empty-state">
              <svg class="empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
              <p>No document loaded</p>
              <p class="empty-subtitle">Upload a PDF to preview</p>
            </div>
          }

          @if (isLoadingPdf) {
            <div class="pdf-loading-state">
              <div class="loading-icon-wrapper">
                <div class="beat-loader">
                  <div class="beat"></div>
                  <div class="beat"></div>
                  <div class="beat"></div>
                </div>
              </div>
              <p class="loading-text">Loading PDF...</p>
            </div>
          }

          @if (loadError) {
            <div class="error-state">
              <svg class="error-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>Failed to load PDF</p>
              <p class="error-subtitle">{{ loadError }}</p>
            </div>
          }

          @if (pdfSrc && !isLoadingPdf && !loadError) {
            <div class="pdf-viewer-wrapper">
              <pdf-viewer 
                [src]="pdfSrc"
                [render-text]="true"
                [original-size]="false"
                [fit-to-page]="false"
                [show-all]="true"
                [page]="currentPage"
                [zoom]="zoom"
                [rotation]="0"
                [autoresize]="true"
                [stick-to-page]="false"
                (after-load-complete)="onPdfLoad($event)"
                (error)="onPdfError($event)"
                (page-rendered)="onPageRendered($event)"
                (pageChange)="onPageChange($event)"
                style="width: 100%; height: 100%;">
              </pdf-viewer>

              @if (isTrainingSnipActive) {
                <div
                  class="training-snip-overlay"
                  [class.is-capturing]="isTrainingSnipCapturing"
                  (pointerdown)="onTrainingSnipPointerDown($event)"
                  (pointermove)="onTrainingSnipPointerMove($event)"
                  (pointerup)="onTrainingSnipPointerUp($event)"
                  (pointerleave)="onTrainingSnipPointerLeave($event)">
                  <div class="snip-instruction-bar" (pointerdown)="$event.stopPropagation()">
                    <span class="snip-instruction-text">Drag to select the screenshot area</span>
                    <button type="button" class="snip-cancel-btn" (click)="cancelTrainingSnipCapture($event)">Cancel</button>
                  </div>

                  @if (trainingSnipSelection) {
                    <div
                      class="training-snip-selection"
                      [style.left.px]="trainingSnipSelection.left"
                      [style.top.px]="trainingSnipSelection.top"
                      [style.width.px]="trainingSnipSelection.width"
                      [style.height.px]="trainingSnipSelection.height">
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    </section>

    @if (canResizeSplitter('viewer-tuning')) {
      <button
        type="button"
        class="column-resizer column-resizer-right"
        title="Resize preview and tuning panel"
        aria-label="Resize preview and tuning panel"
        (pointerdown)="startColumnResize($event, 'viewer-tuning')">
        <span class="column-resizer-grip" aria-hidden="true"></span>
      </button>
    }
    
    <!-- Column 3: Tuning Section -->
    <section class="column tuning-column">
      <div class="card tuning-card">
        <div class="tuning-header">
          <div class="tuning-tabs" role="tablist" aria-label="Prompt tuning panel tabs">
            <button
              class="tuning-tab"
              [class.active]="activeTuningTab === 'prompt'"
              [attr.aria-selected]="activeTuningTab === 'prompt'"
              role="tab"
              type="button"
              (click)="switchTuningTab('prompt')">
              Prompt Tuning
            </button>
            <button
              class="tuning-tab"
              [class.active]="activeTuningTab === 'training'"
              [attr.aria-selected]="activeTuningTab === 'training'"
              role="tab"
              type="button"
              (click)="switchTuningTab('training')">
              Training
            </button>
          </div>
          @if (activeTuningTab === 'prompt' && hasPromptChanges()) {
            <div class="action-buttons-container">
              <button class="btn-save-changes" 
                      (click)="savePromptChanges()">
                <span class="material-icons">save</span>
                Save Changes
              </button>
            </div>
          }
        </div>
        @if (activeTuningTab === 'prompt') {
        <div class="prompt-tuning-form">
          <!-- Field Name -->
          <div class="attribute-value-pair">
            <div class="attribute-header">
              <div class="attribute-info">
                <div class="attribute-name">
                  <div class="attribute-label">
                    <span class="label-text">Field Name</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="truth-value-container">
              <div class="autocomplete-wrapper">
                <input type="text"
                       class="autocomplete-input"
                       [(ngModel)]="fieldNameInput"
                       (input)="onFieldNameInput()"
                       (focus)="onFieldNameFocus()"
                       (blur)="onFieldNameBlur()"
                       (keydown)="onFieldNameKeydown($event)"
                       [placeholder]="!selectedFile ? 'Select a file first...' : (loadingFieldNames ? 'Loading fields...' : 'Type to search or select a field...')"
                       [disabled]="!selectedFile || loadingFieldNames"
                       autocomplete="off">
                <svg class="dropdown-arrow-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
                @if (showFieldNameDropdown && !loadingFieldNames && filteredFieldNames.length > 0) {
                  <div class="autocomplete-dropdown">
                    @for (fieldName of filteredFieldNames; track fieldName) {
                      <div class="autocomplete-option" 
                           (mousedown)="selectFieldName(fieldName)"
                           [class.selected]="fieldName === selectedFieldName">
                        {{ fieldName }}
                      </div>
                    }
                  </div>
                }
                @if (showFieldNameDropdown && !loadingFieldNames && filteredFieldNames.length === 0) {
                  <div class="autocomplete-dropdown">
                    <div class="autocomplete-option no-results">
                      No matching fields found
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Current Prompt (READ-ONLY) -->
          <div class="attribute-value-pair">
            <div class="attribute-header">
              <div class="attribute-info">
                <div class="attribute-name">
                  <div class="attribute-label">
                    <span class="label-text">Current Prompt</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="truth-value-container">
              <div class="value-box-readonly" [class.disabled-state]="!selectedFieldName">
                <div class="readonly-prompt-display">@if (currentPrompt) {{{ currentPrompt }}} @else {
                    <span class="placeholder-text">
                      @if (selectedFieldName) {
                        No current prompt available
                      } @else {
                        Select a field to view prompt...
                      }
                    </span>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Revised Prompt -->
          <div class="attribute-value-pair">
            <div class="attribute-header">
              <div class="attribute-info">
                <div class="attribute-name">
                  <div class="attribute-label">
                    <span class="label-text">Revised prompt/past prompts</span>
                  </div>
                </div>
                
                <!-- HISTORY BUTTON WITH DROPDOWN -->
                <div class="dropdown-wrapper">
                  <button class="btn-view-versions" 
                          (click)="toggleVersionsDropdown($event)"
                          [disabled]="!selectedFieldName"
                          title="View version history">
                    <span class="material-icons">history</span>
                  </button>
                  
                  <!-- VERSIONS DROPDOWN POPUP -->
                  @if (showVersionsDropdown && selectedFieldName) {
                    <div class="versions-dropdown-popup">
                      @if (loadingVersions) {
                        <div class="versions-loading">
                          <div class="beat-loader">
                            <div class="beat"></div>
                            <div class="beat"></div>
                            <div class="beat"></div>
                          </div>
                          <span class="loading-text">Loading versions...</span>
                        </div>
                      } @else if (versionHistory.length > 0) {
                        <div class="versions-list">
                          @for (version of versionHistory; track version.uuid) {
                            <div class="version-item" (click)="selectVersion(version)">
                              <div class="version-timestamp">
                                {{ formatDateTime(version.modified_at) }}
                              </div>
                            </div>
                          }
                        </div>
                      } @else {
                        <div class="versions-empty">No version history available</div>
                      }
                    </div>
                  }
                </div>
                
                <!-- AI BUTTON -->
                <button class="btn-ai-generate" 
                        (click)="handleAIAction()"
                        [disabled]="true"
                        title="AI feature (Coming soon)">
                  <span class="ai-text">AI</span>
                </button>
              </div>
            </div>
            
            <div class="truth-value-container">
              <!-- TEXTAREA + UNDO -->
              <div class="truth-value-wrapper">
                <div class="value-box-edit" [class.disabled-state]="!selectedFieldName">
                  <textarea class="truth-value-edit-input prompt-textarea"
                            [(ngModel)]="revisedPrompt"
                            (input)="autoResizeTextarea($event)"
                            [disabled]="!selectedFieldName"
                            placeholder="Select a field to edit prompt..."
                            rows="5"></textarea>
                </div>
                
                <!-- UNDO BUTTON -->
                @if (isRevisedPromptModified()) {
                  <button class="btn-undo-side" 
                          (click)="undoRevisedPrompt()"
                          title="Undo changes">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 7v6h6"></path>
                      <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"></path>
                    </svg>
                  </button>
                }
              </div>
              
            </div>
          </div>


        </div>
        } @else {
        <div class="training-chat-shell">
          <div class="training-chat-timeline" #trainingTimeline>
            @if (trainingMessages.length === 0) {
              <div class="training-empty-state">
                @if (selectedFile) {
                  <p>Start a training conversation for this file.</p>
                } @else {
                  <p>Select a file to include preview context in training.</p>
                }
              </div>
            } @else {
              @for (message of trainingMessages; track message.id) {
                <div class="training-message" [class.user]="message.role === 'user'" [class.assistant]="message.role === 'assistant'">
                  <div class="message-bubble">
                    <div class="message-text" [class.is-typing]="message.isTyping">{{ getRenderedTrainingMessageText(message) }}</div>
                    @if (message.statusType || message.readyDetails) {
                      <div class="message-meta-row">
                        @if (message.statusType) {
                          <div class="message-status" [class.ready]="message.statusType === 'ready'" [class.needs-clarification]="message.statusType === 'needs_clarification'" [class.cancelled]="message.statusType === 'cancelled'">
                            {{ message.statusType === 'ready' ? 'Ready' : (message.statusType === 'needs_clarification' ? 'Needs Clarification' : 'Cancelled') }}
                          </div>
                        }
                        @if (message.readyDetails) {
                          <button type="button" class="message-view-btn" (click)="openTrainingResponseDetails(message)">Click to View</button>
                        }
                      </div>
                    }
                  </div>
                </div>
              }

              @if (isTrainingSending) {
                <div class="training-message assistant thinking-loader-row" aria-live="polite" aria-label="Assistant is preparing a response">
                  <div class="message-bubble thinking-loader-bubble">
                    <div class="thinking-loader-text">
                      <span class="thinking-loader-label">Thinking</span>
                      <span class="thinking-loader-dots" aria-hidden="true">
                        <span class="thinking-dot"></span>
                        <span class="thinking-dot"></span>
                        <span class="thinking-dot"></span>
                      </span>
                    </div>
                  </div>
                </div>
              }

              @if (isTrainingClarificationActive()) {
                <div class="training-clarification-answer-box">
                  <textarea
                    #trainingClarificationTextarea
                    class="training-clarification-input"
                    [(ngModel)]="trainingClarificationInput"
                    (input)="onTrainingClarificationInput($event)"
                    (keydown)="onTrainingClarificationKeydown($event)"
                    [disabled]="isTrainingSending"
                    rows="1"
                    placeholder="Type your answer and click OK"></textarea>
                  <div class="training-clarification-actions">
                    <button type="button" class="training-clarification-ok-btn" [disabled]="isTrainingSending || !trainingClarificationInput.trim()" (click)="submitTrainingClarificationAnswer()">OK</button>
                  </div>
                </div>
              }
            }
          </div>

          <div class="training-composer">
            <div class="chat-compose-shell">
              <div class="training-shell-context composer-line context-line" [class.has-attachment]="trainingContextAttachment">
                @if (trainingContextAttachment && trainingContextAttachment.type === 'screenshot') {
                  <button class="context-indicator-chip is-screenshot" type="button" [title]="trainingContextAttachment.label" (click)="openTrainingScreenshotPreview()">
                    <span class="material-icons">photo</span>
                  </button>
                } @else {
                  <span class="context-indicator-chip is-preview" [title]="getTrainingContextLineText()">
                    <span class="material-icons">description</span>
                  </span>
                }

                @if (trainingContextAttachment && trainingContextAttachment.type === 'screenshot') {
                  <button class="context-remove-btn" type="button" title="Remove screenshot" (click)="removeTrainingContextAttachment()">
                    <span class="material-icons">close</span>
                  </button>
                }
              </div>

              <div class="composer-line input-line">
                <textarea
                  #trainingComposerInput
                  class="training-input"
                  [(ngModel)]="trainingMessageInput"
                  (input)="onTrainingInput($event)"
                  (keydown)="onTrainingComposerKeydown($event)"
                  [disabled]="isTrainingSending || isTrainingClarificationActive()"
                  rows="1"
                  placeholder="Chat here"></textarea>
              </div>

              <div class="composer-line action-line">
                <div class="action-left">
                  <button class="mini-plus-btn" type="button" title="Take screenshot snip" [disabled]="!selectedFile" (click)="startTrainingSnipCapture()">
                    <span class="material-icons">photo_camera</span>
                  </button>

                  <button class="mini-context-btn" type="button" title="Context" [disabled]="!selectedFile" [class.active]="hasTrainingContextValue" (click)="openTrainingContextModal()">
                    <span class="material-icons">subject</span>
                  </button>
                </div>

                <div class="action-right">
                  <button
                    class="send-arrow-btn"
                    type="button"
                    [class.stop-state]="isTrainingStopActionVisible"
                    [title]="isTrainingStopActionVisible ? 'Stop' : 'Send'"
                    [disabled]="isTrainingPrimaryActionDisabled"
                    (click)="onTrainingPrimaryAction()">
                    <span class="material-icons">{{ isTrainingStopActionVisible ? 'stop_circle' : 'north' }}</span>
                  </button>
                </div>
              </div>
            </div>

            <input
              #trainingScreenshotInput
              type="file"
              accept="image/*"
              style="display: none"
              (change)="onTrainingScreenshotSelected($event)">

          </div>

          @if (isTrainingContextModalOpen) {
            <div class="training-context-inline-backdrop" (click)="closeTrainingContextModal($event)">
              <div class="training-context-modal" (click)="$event.stopPropagation()">
                <div class="training-screenshot-modal-header">
                  <span class="training-screenshot-modal-title">Context</span>
                  <button type="button" class="training-screenshot-modal-close" title="Close" (click)="closeTrainingContextModal($event)">
                    <span class="material-icons">close</span>
                  </button>
                </div>
                <div class="training-context-modal-body">
                  <textarea
                    class="training-context-modal-input"
                    [(ngModel)]="trainingContextDraftInput"
                    [disabled]="isTrainingSending"
                    rows="6"
                    placeholder="Context"></textarea>
                </div>
                <div class="training-context-modal-footer">
                  <button type="button" class="training-response-cancel-btn" (click)="closeTrainingContextModal($event)">Cancel</button>
                  <button type="button" class="training-response-generate-btn" [disabled]="isTrainingSending" (click)="saveTrainingContextModal($event)">Save</button>
                </div>
              </div>
            </div>
          }
        </div>
        }
      </div>
    </section>
    
  </div>
</div>

@if (isTrainingScreenshotModalOpen) {
  <div class="training-screenshot-modal-backdrop" (click)="closeTrainingScreenshotPreview($event)">
    <div class="training-screenshot-modal" (click)="$event.stopPropagation()">
      <div class="training-screenshot-modal-header">
        <span class="training-screenshot-modal-title">{{ trainingScreenshotModalTitle }}</span>
        <button type="button" class="training-screenshot-modal-close" title="Close" (click)="closeTrainingScreenshotPreview($event)">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="training-screenshot-modal-body">
        <img [src]="trainingScreenshotModalUrl" alt="Captured screenshot preview" class="training-screenshot-preview-image">
      </div>
    </div>
  </div>
}

@if (isTrainingResponseModalOpen && trainingSelectedReadyDetailsList.length > 0) {
  <div class="training-response-modal-backdrop" (click)="closeTrainingResponseDetails($event)">
    <div class="training-response-modal" (click)="$event.stopPropagation()">
      <div class="training-response-modal-header">
        <span class="training-response-modal-title">Field Details</span>
        <button type="button" class="training-response-modal-close" title="Close" (click)="closeTrainingResponseDetails($event)">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="training-response-modal-body">
        <div class="training-response-details">
          <div class="training-response-table-wrap">
            <table class="training-response-table horizontal">
              <thead>
                <tr>
                  <th>Field Name</th>
                  <th>Type</th>
                  <th>Feature</th>
                  <th>Prompt</th>
                  <th>Additional Prompt</th>
                  <th>Field Schema</th>
                  <th>Compute Schema</th>
                  <th>External Key</th>
                </tr>
              </thead>
              <tbody>
                @for (readyDetail of trainingSelectedReadyDetailsList; track $index) {
                  <tr>
                    <td>
                      <div class="training-response-cell-edit-wrap" [class.training-response-cell-invalid]="isTrainingModalFieldNameConflict($index)" [class.training-response-cell-required-empty]="isTrainingModalRequiredFieldEmpty(readyDetail, 'fieldName')">
                        <input type="text" class="training-response-cell-input" [(ngModel)]="readyDetail.fieldName" (ngModelChange)="onTrainingResponseDetailsChanged()" [title]="readyDetail.fieldName || null">
                        <span class="training-response-cell-pen" aria-hidden="true">
                          <svg class="training-response-cell-pen-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/><path d="M15 5l4 4"/></svg>
                        </span>
                      </div>
                    </td>
                    <td>
                      <div class="training-response-cell-edit-wrap" [class.training-response-cell-required-empty]="isTrainingModalRequiredFieldEmpty(readyDetail, 'fieldType')">
                        <select class="training-response-cell-select" [(ngModel)]="readyDetail.fieldType" (ngModelChange)="onTrainingReadyDetailTypeChanged(readyDetail)">
                          @for (fieldType of trainingFieldTypeOptions; track fieldType) {
                            <option [value]="fieldType">{{ getTrainingFieldTypeLabel(fieldType) }}</option>
                          }
                        </select>
                      </div>
                    </td>
                    <td>
                      @if (getTrainingFeatureOptions(readyDetail.fieldType).length > 0) {
                        <div class="training-response-cell-edit-wrap" [class.training-response-cell-required-empty]="isTrainingModalRequiredFieldEmpty(readyDetail, 'feature')">
                          <select class="training-response-cell-select" [(ngModel)]="readyDetail.feature" (ngModelChange)="onTrainingResponseDetailsChanged()">
                            <option value="">-- Select --</option>
                            @for (featureOption of getTrainingFeatureOptions(readyDetail.fieldType); track featureOption) {
                              <option [value]="featureOption">{{ featureOption }}</option>
                            }
                          </select>
                        </div>
                      } @else {
                        <div class="training-response-cell-edit-wrap training-response-cell-edit-wrap-disabled">
                          <input type="text" class="training-response-cell-input" value="" disabled>
                        </div>
                      }
                    </td>
                    <td>
                      <div
                        class="training-response-cell-display"
                        [class.training-response-cell-required-empty]="isTrainingModalRequiredFieldEmpty(readyDetail, 'prompt')"
                        [class.training-response-cell-display-disabled]="isTrainingModalFieldReadOnly(readyDetail, 'prompt')"
                        [class.training-response-cell-display-clickable]="!isTrainingModalFieldReadOnly(readyDetail, 'prompt')"
                        [class.training-response-cell-display-frozen]="isTrainingModalFieldReadOnly(readyDetail, 'prompt')"
                        (click)="openTrainingFieldPopup($index, 'prompt')">
                        <span class="training-response-cell-text" [title]="readyDetail.prompt">{{ readyDetail.prompt || '—' }}</span>
                        @if (!isTrainingModalFieldReadOnly(readyDetail, 'prompt')) {
                          <span class="training-response-cell-pen" aria-hidden="true">
                            <svg class="training-response-cell-pen-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/><path d="M15 5l4 4"/></svg>
                          </span>
                        }
                      </div>
                    </td>
                    <td>
                      <div
                        class="training-response-cell-display"
                        [class.training-response-cell-required-empty]="isTrainingModalRequiredFieldEmpty(readyDetail, 'additionalPrompt')"
                        [class.training-response-cell-display-disabled]="isTrainingModalFieldReadOnly(readyDetail, 'additionalPrompt')"
                        [class.training-response-cell-display-clickable]="!isTrainingModalFieldReadOnly(readyDetail, 'additionalPrompt')"
                        [class.training-response-cell-display-frozen]="isTrainingModalFieldReadOnly(readyDetail, 'additionalPrompt')"
                        (click)="openTrainingFieldPopup($index, 'additionalPrompt')">
                        <span class="training-response-cell-text" [title]="readyDetail.additionalPrompt">{{ readyDetail.additionalPrompt || '—' }}</span>
                        @if (!isTrainingModalFieldReadOnly(readyDetail, 'additionalPrompt')) {
                          <span class="training-response-cell-pen" aria-hidden="true">
                            <svg class="training-response-cell-pen-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/><path d="M15 5l4 4"/></svg>
                          </span>
                        }
                      </div>
                    </td>
                    <td>
                      <div
                        class="training-response-cell-display training-response-cell-code"
                        [class.training-response-cell-required-empty]="isTrainingModalRequiredFieldEmpty(readyDetail, 'fieldSchema')"
                        [class.training-response-cell-display-disabled]="isTrainingModalFieldReadOnly(readyDetail, 'fieldSchema')"
                        [class.training-response-cell-display-clickable]="!isTrainingModalFieldReadOnly(readyDetail, 'fieldSchema')"
                        [class.training-response-cell-display-frozen]="isTrainingModalFieldReadOnly(readyDetail, 'fieldSchema')"
                        (click)="openTrainingFieldPopup($index, 'fieldSchema')">
                        <span class="training-response-cell-text" [title]="readyDetail.fieldSchema">{{ readyDetail.fieldSchema || '—' }}</span>
                        @if (!isTrainingModalFieldReadOnly(readyDetail, 'fieldSchema')) {
                          <span class="training-response-cell-pen" aria-hidden="true">
                            <svg class="training-response-cell-pen-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/><path d="M15 5l4 4"/></svg>
                          </span>
                        }
                      </div>
                    </td>
                    <td>
                      <div
                        class="training-response-cell-display training-response-cell-code"
                        [class.training-response-cell-required-empty]="isTrainingModalRequiredFieldEmpty(readyDetail, 'computeSchema')"
                        [class.training-response-cell-display-disabled]="isTrainingModalFieldReadOnly(readyDetail, 'computeSchema')"
                        [class.training-response-cell-display-clickable]="!isTrainingModalFieldReadOnly(readyDetail, 'computeSchema')"
                        [class.training-response-cell-display-frozen]="isTrainingModalFieldReadOnly(readyDetail, 'computeSchema')"
                        (click)="openTrainingFieldPopup($index, 'computeSchema')">
                        <span class="training-response-cell-text" [title]="readyDetail.computeSchema">{{ readyDetail.computeSchema || '—' }}</span>
                        @if (!isTrainingModalFieldReadOnly(readyDetail, 'computeSchema')) {
                          <span class="training-response-cell-pen" aria-hidden="true">
                            <svg class="training-response-cell-pen-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/><path d="M15 5l4 4"/></svg>
                          </span>
                        }
                      </div>
                    </td>
                    <td>
                      <div class="training-response-cell-edit-wrap training-response-cell-edit-wrap-external-key">
                        <input type="text" class="training-response-cell-input training-response-cell-input-external-key" [(ngModel)]="readyDetail.externalKey" (ngModelChange)="onTrainingResponseDetailsChanged()" [title]="readyDetail.externalKey || null">
                        <span class="training-response-cell-pen" aria-hidden="true">
                          <svg class="training-response-cell-pen-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/><path d="M15 5l4 4"/></svg>
                        </span>
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="training-response-modal-footer">
        @if (hasTrainingResponseFieldNameConflicts) {
          <p class="training-response-warning-msg">A field with this name already exists</p>
        }
        <button type="button" class="training-response-cancel-btn" [disabled]="isTrainingResponseContinueDisabled" (click)="continueTrainingResponseDetails($event)">Continue Chat</button>
        <button type="button" class="training-response-generate-btn" [disabled]="isTrainingGenerateActionDisabled" (click)="generateTrainingFieldDirectly()">Generate</button>
        <button type="button" class="training-response-generate-btn" [disabled]="isTrainingGenerateActionDisabled" (click)="confirmTrainingResponseGenerate()">Generate and Review</button>
      </div>
    </div>
  </div>
}

@if (isTrainingFieldPopupOpen && isTrainingResponseModalOpen) {
  <div class="training-field-popup-overlay" (click)="closeTrainingFieldPopup($event)">
    <div class="training-field-popup-card" (click)="$event.stopPropagation()">
      <div class="training-field-popup-header">
        <div class="training-field-popup-context">
          <span class="training-field-popup-field">{{ trainingFieldPopupFieldName }}</span>
          <span class="training-field-popup-label">{{ trainingFieldPopupColumnLabel }}</span>
        </div>
        <div class="training-field-popup-toolbar">
          <button class="training-field-popup-tool-btn" type="button" (click)="undoTrainingFieldPopupValue($event)" title="Undo changes to this field">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
            </svg>
            Undo
          </button>
          <button class="training-field-popup-tool-btn training-field-popup-tool-clear" type="button" (click)="clearTrainingFieldPopupValue($event)" title="Clear this field">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Clear
          </button>
          <button class="training-field-popup-tool-btn training-field-popup-tool-copy" type="button" (click)="copyTrainingFieldPopupValue($event)" title="Copy this field value">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
            Copy
          </button>
        </div>
        <button type="button" class="training-field-popup-close" title="Close" (click)="closeTrainingFieldPopup($event)">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="training-field-popup-body">
        <textarea
          class="training-field-popup-textarea"
          [class.training-field-popup-textarea-code]="isTrainingFieldPopupSchemaColumn"
          [class.training-field-popup-textarea-invalid]="!!trainingFieldPopupError"
          [(ngModel)]="trainingFieldPopupDraftValue"
          (ngModelChange)="onTrainingFieldPopupDraftChanged()"
          rows="10"
          placeholder="Enter value"></textarea>
        @if (trainingFieldPopupError) {
          <p class="training-field-popup-error">{{ trainingFieldPopupError }}</p>
        }
        @if (shouldShowTrainingFieldSchemaUploadButton) {
          <input #trainingFieldSchemaUploadInput type="file" accept=".json" (change)="handleTrainingFieldPopupJsonUpload($event)" style="display: none;" />
          <button
            type="button"
            class="training-field-popup-upload-label"
            [class.training-field-popup-upload-label-loading]="isTrainingFieldPopupJsonUploading"
            [disabled]="isTrainingFieldPopupJsonUploading"
            (click)="startTrainingFieldPopupJsonUpload(trainingFieldSchemaUploadInput, $event)">
            <span class="training-field-popup-upload-idle" [hidden]="isTrainingFieldPopupJsonUploading">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Upload JSON
            </span>
            <span class="training-field-popup-upload-loading" [hidden]="!isTrainingFieldPopupJsonUploading">
              <span class="training-field-popup-upload-spinner"></span>
              Uploading…
            </span>
          </button>
        }
        @if (shouldShowTrainingComputeSchemaUploadButton) {
          <input #trainingComputeSchemaUploadInput type="file" accept=".json" (change)="handleTrainingFieldPopupJsonUpload($event)" style="display: none;" />
          <button
            type="button"
            class="training-field-popup-upload-label"
            [class.training-field-popup-upload-label-loading]="isTrainingFieldPopupJsonUploading"
            [disabled]="isTrainingFieldPopupJsonUploading"
            (click)="startTrainingFieldPopupJsonUpload(trainingComputeSchemaUploadInput, $event)">
            <span class="training-field-popup-upload-idle" [hidden]="isTrainingFieldPopupJsonUploading">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Upload JSON
            </span>
            <span class="training-field-popup-upload-loading" [hidden]="!isTrainingFieldPopupJsonUploading">
              <span class="training-field-popup-upload-spinner"></span>
              Uploading…
            </span>
          </button>
        }
      </div>

      <div class="training-field-popup-footer">
        <button type="button" class="training-response-cancel-btn" (click)="closeTrainingFieldPopup($event)">Cancel</button>
        <button type="button" class="training-response-generate-btn" [disabled]="isTrainingFieldPopupDoneDisabled" (click)="applyTrainingFieldPopup($event)">Done</button>
      </div>
    </div>
  </div>
}

